/**
 * Shared WebVTT helpers for caption list handling (one-time caption translation, legacy alignment).
 */

function pad(num: number): string {
  return num < 10 ? `0${num}` : `${num}`;
}

export function convertTimeToSeconds(time: string): number {
  const parts = time.split(":");
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  const secParts = (parts[2] || "0").split(".");
  const seconds = parseInt(secParts[0], 10) || 0;
  const ms = parseInt(secParts[1]?.padEnd(3, "0").slice(0, 3), 10) || 0;
  return hours * 3600 + minutes * 60 + seconds + ms / 1000;
}

export function convertSecondsToTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secsNumber = seconds % 60;
  const secs =
    secsNumber < 10 ? `0${secsNumber.toFixed(3)}` : secsNumber.toFixed(3);
  return `${pad(hours)}:${pad(minutes)}:${secs}`;
}

export interface CaptionItem {
  start: number;
  dur: string;
  text: string;
  [key: string]: unknown;
}

const WEBVTT_CUE_TIMESTAMP_RE =
  /(\d{2}:){2}\d{2}\.\d{3}\s*-->\s*(\d{2}:){2}\d{2}\.\d{3}/;

/**
 * Extract caption text lines from WebVTT (one entry per text line inside a cue).
 * Matches audio/video parseWebVTT() used for translateCaptions API payloads.
 */
export function extractWebVttCaptionTexts(webvttContent: string): string[] {
  const lines = webvttContent.split("\n");
  let isCaptionsSection = false;
  const sentences: string[] = [];

  for (const line of lines) {
    if (WEBVTT_CUE_TIMESTAMP_RE.test(line)) {
      isCaptionsSection = true;
    } else if (
      isCaptionsSection &&
      line.trim() !== "" &&
      !/^\d+$/.test(line)
    ) {
      sentences.push(line);
    }
  }
  return sentences;
}

/** True when WebVTT has at least one cue with non-empty text (not just "WEBVTT" header). */
export function hasWebVttCaptions(webvttContent: string): boolean {
  return extractWebVttCaptionTexts(webvttContent).some((line) => line.trim().length > 0);
}

/**
 * Parse WebVTT content into caption objects { start (seconds), dur (seconds as string), text }.
 */
export function parseWebVTTToCaption(webvttContent: string): CaptionItem[] {
  try {
    const captions: CaptionItem[] = [];
    const webvttLines = webvttContent.split("\n").map((line) => line.trim());
    let index = 0;
    while (index < webvttLines.length) {
      const line = webvttLines[index];
      if (line === "WEBVTT" || line === "" || /^\d+$/.test(line)) {
        index++;
        continue;
      }
      const timeCode = webvttLines[index];
      const [startStr, durStr] = timeCode.split(" --> ");
      const textLines: string[] = [];
      index++;
      while (index < webvttLines.length && webvttLines[index] !== "") {
        textLines.push(webvttLines[index]);
        index++;
      }
      const start = convertTimeToSeconds(startStr);
      const end = convertTimeToSeconds(durStr);
      captions.push({
        start,
        dur: (end - start).toFixed(3),
        text: textLines.join(" "),
      });
      index++;
    }
    return captions;
  } catch (error) {
    console.error("Error parsing WebVTT:", error);
    return [];
  }
}

/**
 * Build WebVTT string from caption list (for translated captions – one text per cue).
 */
export function convertCaptionToWebVTT(captions: CaptionItem[]): string {
  try {
    let webvttContent = "WEBVTT\n\n";
    captions.forEach((caption, i) => {
      const startTime = convertSecondsToTime(caption.start);
      const endTime = convertSecondsToTime(
        caption.start + parseFloat(String(caption.dur))
      );
      webvttContent += `${i + 1}\n`;
      webvttContent += `${startTime} --> ${endTime}\n`;
      webvttContent += `${caption.text}\n\n`;
    });
    return webvttContent;
  } catch (error) {
    console.error("Error converting captions to WebVTT:", error);
    return "";
  }
}

/**
 * Normalize translation.segments to a list of strings (one per caption).
 * If API sent one blob and there are multiple original captions, split by original caption length ratio.
 */
export function segmentsListForCaptions(
  rawSegments: unknown,
  originalCaptions: CaptionItem[]
): string[] {
  const list: string[] = Array.isArray(rawSegments)
    ? rawSegments.map((s: unknown) => (typeof s === "string" ? s : String(s ?? "")))
    : rawSegments != null && rawSegments !== ""
      ? [String(rawSegments)]
      : [];
  if (list.length === 0) return [];
  if (list.length >= originalCaptions.length) return list;
  if (list.length === 1 && originalCaptions.length > 1) {
    const oneBlob = list[0];
    const origLengths = originalCaptions.map((c) => (c.text || "").length);
    const total = origLengths.reduce((a, b) => a + b, 0) || 1;
    const out: string[] = [];
    let pos = 0;
    for (let i = 0; i < originalCaptions.length; i++) {
      const ratio = origLengths[i] / total;
      const len =
        i === originalCaptions.length - 1
          ? oneBlob.length - pos
          : Math.round(oneBlob.length * ratio);
      out.push(oneBlob.slice(pos, pos + len).trim() || oneBlob);
      pos += len;
    }
    return out;
  }
  return list;
}
